import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from "@/api/apiClient";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, BellOff, BellRing, Mail, ArrowLeft, RotateCcw } from "lucide-react";
import { toast } from "sonner";

type CategoryType = 'price_drops' | 'restock' | 'new_from_subscribed' | 'all';
type PreferenceKey = Exclude<CategoryType, 'all'>;

const PREFERENCE_KEYS: PreferenceKey[] = ['price_drops', 'restock', 'new_from_subscribed'];

interface UnsubscribeData {
    maskedEmail: string;
    category: CategoryType;
    preferences: Record<PreferenceKey, boolean>;
}

const CATEGORY_LABELS: Record<CategoryType, string> = {
    price_drops: 'Alertes baisses de prix',
    restock: 'Alertes restock et tailles',
    new_from_subscribed: 'Nouveautés de marques suivies',
    all: 'Toutes les notifications par email'
};

export const UnsubscribePage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [status, setStatus] = useState<'loading' | 'ready' | 'submitting' | 'success' | 'resubscribed' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [data, setData] = useState<UnsubscribeData | null>(null);
    const [choice, setChoice] = useState<'category' | 'all'>('category');
    // Alerts that were active before this unsubscribe: the only ones "undo" may re-enable
    const [restorable, setRestorable] = useState<PreferenceKey[]>([]);
    const [isResubscribing, setIsResubscribing] = useState(false);

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setErrorMessage('Lien de désabonnement incomplet ou token manquant.');
            return;
        }

        const verify = async () => {
            try {
                const res = await apiClient.unsubscribe.verify(token);
                if (res.success && res.data) {
                    setData(res.data);
                    if (res.data.category === 'all') {
                        setChoice('all');
                    } else {
                        setChoice('category');
                    }
                    setStatus('ready');
                } else {
                    setStatus('error');
                    setErrorMessage(res.error || 'Lien de désabonnement invalide.');
                }
            } catch (err: any) {
                console.error('Erreur vérification token:', err);
                setStatus('error');
                setErrorMessage(err.response?.data?.error || 'Lien de désabonnement expiré ou invalide.');
            }
        };

        verify();
    }, [token]);

    const handleConfirmUnsubscribe = async () => {
        if (!token || !data) return;
        setStatus('submitting');
        try {
            const isAll = choice === 'all' || data.category === 'all';
            const targetKeys: PreferenceKey[] = isAll ? PREFERENCE_KEYS : [data.category as PreferenceKey];

            const res = await apiClient.unsubscribe.confirm(token, isAll ? { all: true } : { category: data.category });

            if (res.success) {
                setRestorable(targetKeys.filter((key) => data.preferences[key]));
                setStatus('success');
                toast.success('Désabonnement effectué avec succès.');
            } else {
                toast.error(res.error || 'Une erreur est survenue.');
                setStatus('ready');
            }
        } catch (err: any) {
            console.error('Erreur désabonnement:', err);
            toast.error(err.response?.data?.error || 'Une erreur est survenue.');
            setStatus('ready');
        }
    };

    const handleResubscribe = async () => {
        if (!token || restorable.length === 0) return;
        setIsResubscribing(true);
        try {
            const res = await apiClient.unsubscribe.resubscribe(token, { categories: restorable });

            if (res.success) {
                setStatus('resubscribed');
                toast.success('Notifications réactivées !');
            } else {
                toast.error(res.error || 'Impossible de réactiver.');
            }
        } catch (err: any) {
            console.error('Erreur réabonnement:', err);
            toast.error(err.response?.data?.error || 'Impossible de réactiver.');
        } finally {
            setIsResubscribing(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-[hsl(228,14%,6%)] text-gray-900 dark:text-gray-100">
            <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full max-w-md"
            >
                <Card className="border border-gray-200 dark:border-gray-800 shadow-xl bg-white/90 dark:bg-gray-900/90 backdrop-blur-md">
                    <CardHeader className="text-center pb-2">
                        <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/60 flex items-center justify-center text-amber-600 dark:text-amber-400 mb-3">
                            <BellOff className="w-6 h-6" />
                        </div>
                        <CardTitle className="text-xl font-bold dark:text-white">
                            Gestion des e-mails
                        </CardTitle>
                        <CardDescription className="dark:text-gray-400 text-sm">
                            Personnalisez vos préférences de notification Pépite
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-4 space-y-4">
                        <AnimatePresence mode="wait">
                            {status === 'loading' && (
                                <motion.div
                                    key="loading"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="flex flex-col items-center justify-center py-8 space-y-3"
                                >
                                    <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Vérification du lien sécurisé...</p>
                                </motion.div>
                            )}

                            {status === 'error' && (
                                <motion.div
                                    key="error"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="text-center py-6 space-y-4"
                                >
                                    <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/50 flex items-center justify-center text-red-600 dark:text-red-400">
                                        <XCircle className="w-6 h-6" />
                                    </div>
                                    <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                                        {errorMessage || 'Ce lien de désabonnement est invalide ou corrompu.'}
                                    </p>
                                    <Button
                                        variant="outline"
                                        onClick={() => navigate('/')}
                                        className="w-full mt-2"
                                    >
                                        <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
                                    </Button>
                                </motion.div>
                            )}

                            {(status === 'ready' || status === 'submitting') && data && (
                                <motion.div
                                    key="ready"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="space-y-4"
                                >
                                    <div className="flex items-center gap-2 p-3 bg-gray-100/70 dark:bg-gray-800/60 rounded-lg text-xs text-gray-600 dark:text-gray-300">
                                        <Mail className="w-4 h-4 text-amber-600 shrink-0" />
                                        <span>Compte concerné : <strong className="font-mono text-gray-800 dark:text-gray-100">{data.maskedEmail}</strong></span>
                                    </div>

                                    <div className="space-y-2">
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Que souhaitez-vous faire ?
                                        </p>

                                        {data.category !== 'all' && (
                                            <label
                                                className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                                                    choice === 'category'
                                                        ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20 shadow-sm'
                                                        : 'border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="unsub_choice"
                                                    checked={choice === 'category'}
                                                    onChange={() => setChoice('category')}
                                                    className="mt-1 text-amber-600 focus:ring-amber-500"
                                                />
                                                <div className="text-sm">
                                                    <div className="font-semibold text-gray-900 dark:text-white">
                                                        Désactiver uniquement {CATEGORY_LABELS[data.category] || data.category}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        Vous continuerez à recevoir les autres notifications utiles.
                                                    </div>
                                                </div>
                                            </label>
                                        )}

                                        <label
                                            className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                                                choice === 'all'
                                                    ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20 shadow-sm'
                                                    : 'border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="unsub_choice"
                                                checked={choice === 'all'}
                                                onChange={() => setChoice('all')}
                                                className="mt-1 text-amber-600 focus:ring-amber-500"
                                            />
                                            <div className="text-sm">
                                                <div className="font-semibold text-gray-900 dark:text-white">
                                                    Ne plus recevoir aucun email
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Désactive toutes les alertes de prix, restocks et nouveautés.
                                                </div>
                                            </div>
                                        </label>
                                    </div>

                                    <Button
                                        onClick={handleConfirmUnsubscribe}
                                        disabled={status === 'submitting'}
                                        className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 mt-2 transition-all shadow-md"
                                    >
                                        {status === 'submitting' ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...
                                            </>
                                        ) : (
                                            'Confirmer le désabonnement'
                                        )}
                                    </Button>
                                </motion.div>
                            )}

                            {status === 'success' && (
                                <motion.div
                                    key="success"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="text-center py-4 space-y-4"
                                >
                                    <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                                        <CheckCircle2 className="w-6 h-6" />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="font-bold text-gray-900 dark:text-white text-base">
                                            Désabonnement confirmé
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Vos préférences ont été immédiatement mises à jour. Vous ne recevrez plus ces emails.
                                        </p>
                                    </div>

                                    <div className="pt-2 space-y-2">
                                        {restorable.length > 0 && (
                                            <Button
                                                variant="outline"
                                                onClick={handleResubscribe}
                                                disabled={isResubscribing}
                                                className="w-full text-xs text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                                            >
                                                {isResubscribing ? (
                                                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                                ) : (
                                                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                                )}
                                                Une erreur ? Réactiver ces alertes
                                            </Button>
                                        )}

                                        <Button
                                            onClick={() => navigate('/')}
                                            className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
                                        >
                                            Retourner sur Pépite ✨
                                        </Button>
                                    </div>
                                </motion.div>
                            )}

                            {status === 'resubscribed' && (
                                <motion.div
                                    key="resubscribed"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="text-center py-4 space-y-4"
                                >
                                    <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 dark:text-amber-400">
                                        <BellRing className="w-6 h-6" />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="font-bold text-gray-900 dark:text-white text-base">
                                            Notifications réactivées !
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Ravi de vous revoir ! Vous recevrez à nouveau vos alertes Pépite.
                                        </p>
                                    </div>

                                    <Button
                                        onClick={() => navigate('/')}
                                        className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold mt-2"
                                    >
                                        Explorer les pépites du jour ✨
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </CardContent>

                    <CardFooter className="border-t border-gray-100 dark:border-gray-800/80 pt-4 text-center justify-center">
                        <p className="text-[11px] text-gray-400 dark:text-gray-500">
                            Pépite &bull; Mode éthique & marques indépendantes
                        </p>
                    </CardFooter>
                </Card>
            </motion.div>
        </div>
    );
};

export default UnsubscribePage;
